import { FC, RefObject, Suspense, useCallback, useMemo, useRef } from 'react';

import { RequirePermission } from '@gasbi/shared/common/providers/PermissionProvider/components/RequirePermission';
import {
  NavLinkTabsContainer,
  NavTabLink,
} from '@gasbi/shared/web/components/NavLinkTabs';
import { usePageContext } from '@gasbi/shared/web/providers/PageProvider';
import { Outlet, useNavigate, useOutletContext } from 'react-router-dom';

import { BuildingProfileAct } from '@/features/buildingProfileActs/types';
import { UserPermissions } from '@/features/permissions';
import { useShowEditButton } from '@/pages/BuildingProfileActDetails/hooks/useShowEditButton';
import { CustomPortal } from '@/pages/MeteringPointActForm/views/Devices/components/CustomPortal';

import Button from '@mui/joy/Button';

export const ActFormView = {
  Information: '',
  Devices: 'devices',
  Seals: 'seals',
} as const;

type DetailsNavigationProps = {
  buildingProfileAct?: BuildingProfileAct;
};

export type BuildingProfileActDetailsOutletContext = {
  outletRef: RefObject<HTMLDivElement>;
  buildingProfileAct?: BuildingProfileAct;
  isLoading?: boolean;
};

export const useBuildingProfileActDetailsOutletContext =
  (): BuildingProfileActDetailsOutletContext =>
    useOutletContext<BuildingProfileActDetailsOutletContext>();

export const BuildingProfileActDetailsNavigation: FC<
  DetailsNavigationProps
> = ({ buildingProfileAct }) => {
  const navigate = useNavigate();
  const { loading } = usePageContext();
  const outletRef = useRef(null);
  const showEditButton = useShowEditButton();

  const links: NavTabLink[] = useMemo(
    () => [
      { title: 'Загальна інформація', path: ActFormView.Information },
      { title: 'Прилади', path: ActFormView.Devices },
      { title: 'Пломби', path: ActFormView.Seals },
    ],
    [],
  );

  const handleEdit = useCallback(() => {
    if (!buildingProfileAct) return;
    navigate(`../form/${buildingProfileAct.id}`, { relative: 'path' });
  }, [navigate, buildingProfileAct]);

  const outletContext = useMemo(
    () =>
      ({
        outletRef,
        buildingProfileAct,
        isLoading: loading,
      }) satisfies BuildingProfileActDetailsOutletContext,
    [buildingProfileAct, loading],
  );

  return (
    <>
      <CustomPortal containerRef={outletRef}>
        {showEditButton && buildingProfileAct && (
          <RequirePermission name={UserPermissions.BuildingProfilesActsManage}>
            <Button onClick={handleEdit}>Редагувати</Button>
          </RequirePermission>
        )}
      </CustomPortal>
      <NavLinkTabsContainer ref={outletRef} links={links} />
      <Suspense>
        <Outlet context={outletContext} />
      </Suspense>
    </>
  );
};
